import { FlexBox } from '@helloself/hello-self-ui';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { Dispatch, SetStateAction, useState } from 'react';
import * as Yup from 'yup';

import { Button, Heading, Modal } from '../../../../ui';
import { submitFormWithUIFeedback } from '../../helpers';
import { useGetExpertCanHelpWithQuery } from '../../model/profile';
import { FullProfile, ProfilePhoto, PutSuccessResponse } from '../../model/types';
import { SubmitStatus } from '../form-feedback';
import { JobTitleField, LocationField, ProfilePictureField, Pronouns, PronounsField, TitleField } from './basic-info';
import { ExpertiseListEntry } from './expertise';
import { TextAreaWithCount } from './intro';

interface EditModalProps {
    isOpen: boolean;
    onClose: () => void;
    profile: FullProfile;
    handleSubmit: (values: Partial<FullProfile>) => Promise<PutSuccessResponse>;
    handlePhotoSubmit: (values: ProfilePhoto) => Promise<PutSuccessResponse>;
    profilePictureUrl?: string;
    setProfilePicturePreview?: Dispatch<SetStateAction<string>>;
}
export interface ProfileFormValues {
    title: string;
    firstName: string;
    lastName: string;
    location: string;
    pronouns: Pronouns | '';
    jobTitle: string;
    otherTopics: string[];
    aboutMe: string;
}

const aboutMeLimit = 300;
const Schema = Yup.object().shape({
    jobTitle: Yup.string().required('This is a required field'),
    otherTopics: Yup.array().min(1, 'Required field'),
    aboutMe: Yup.string()
        .max(aboutMeLimit, 'Maximum character length exceeded')
        .required('About me is a required field'),
});

export function EditModal({
    isOpen,
    onClose,
    profile,
    handleSubmit,
    handlePhotoSubmit,
    profilePictureUrl,
    setProfilePicturePreview,
}: EditModalProps) {
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [dirty, setDirty] = useState(false);
    // phasing out events for recording field edit
    //const { recordFieldEdit } = useProfileRecordEvent();
    const { data: expertiseList } = useGetExpertCanHelpWithQuery();
    const onCloseEditModal = () => {
        onClose();
    };

    const initialValues: ProfileFormValues = {
        title: profile.title || '',
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        location: profile.location?.toLowerCase() || '',
        pronouns: (profile.pronouns as Pronouns) || '',
        jobTitle: profile.jobTitle || '',
        otherTopics: profile.otherTopics || [],
        aboutMe: profile.aboutMe || '',
    };

    const onSubmit = (values: ProfileFormValues, helpers: FormikHelpers<ProfileFormValues>) => {
        const mappedValues: Partial<FullProfile> = {
            ...values,
        };
        submitFormWithUIFeedback<ProfileFormValues>(mappedValues, helpers, handleSubmit, values);
    };

    const onCloseForm = () => {
        if (dirty) {
            setShowConfirmation(true);
        } else {
            onCloseEditModal();
        }
    };

    return (
        <>
            <Modal show={isOpen} onEscapeKeyDown={onCloseForm}>
                <Formik
                    validateOnChange={true}
                    initialValues={initialValues}
                    validationSchema={Schema}
                    onSubmit={onSubmit}
                >
                    {(props) => {
                        setTimeout(() => {
                            setDirty(props.dirty);
                        });

                        return (
                            <Form>
                                <Modal.Header onHide={onCloseForm}>
                                    <Heading level={3}>Edit profile</Heading>
                                </Modal.Header>
                                <Modal.Body>
                                    <div className="flex flex-col gap-8">
                                        <div>
                                            Let's get your profile set up so you can start receiving personalised
                                            recommendations and connect with the right experts
                                        </div>

                                        <FlexBox gap="32px" flexDirection="column">
                                            {/** Profile picture */}
                                            <ProfilePictureField
                                                imageUrl={profilePictureUrl || ''}
                                                firstName={profile.firstName}
                                                lastName={profile.lastName}
                                                handlePhotoSubmit={handlePhotoSubmit}
                                                setProfilePicturePreview={setProfilePicturePreview}
                                            />
                                            {/** Basic Info */}
                                            <div className="flex flex-col gap-3">
                                                <Heading level={3}>Basic info</Heading>
                                                <div className="flex gap-4">
                                                    <div className="basis-1/2">
                                                        <TitleField {...props} />
                                                    </div>
                                                    <div className="basis-1/2">
                                                        <PronounsField {...props} />
                                                    </div>
                                                </div>
                                                <JobTitleField {...props} />
                                                <LocationField />
                                            </div>

                                            {/** Expertise */}
                                            <div className="flex flex-col gap-3">
                                                <Heading level={3}>Specialisms and areas of interest*</Heading>
                                                <ExpertiseListEntry
                                                    fieldName="otherTopics"
                                                    label="Which presentations do you treat and support your clients with, or are areas of interest?"
                                                    options={expertiseList || []}
                                                    {...props}
                                                    handleBlur={() => {
                                                        props.handleBlur('otherTopics');
                                                        props.setFieldTouched('otherTopics');
                                                    }}
                                                />
                                            </div>

                                            {/** Intro */}
                                            <div className="flex flex-col gap-3">
                                                <Heading level={3}>About me*</Heading>
                                                <TextAreaWithCount
                                                    limit={aboutMeLimit}
                                                    name="aboutMe"
                                                    placeholder="Promote yourself by sharing your approach and experience"
                                                    label="Share a brief introduction to yourself with the network, including your approach to your work, and any experiences that highlight your expertise and areas of interest."
                                                    height="150px"
                                                    {...props}
                                                    handleBlur={() => {
                                                        props.handleBlur('aboutMe');
                                                        props.setFieldTouched('aboutMe');
                                                    }}
                                                />
                                            </div>
                                        </FlexBox>
                                    </div>
                                </Modal.Body>
                                <Modal.Footer>
                                    <div className="flex justify-between">
                                        <div className="h-[42px]">
                                            <SubmitStatus isSubmitting={props.isSubmitting} status={props.status} />
                                        </div>
                                        <Button disabled={!props.dirty} isProcessing={props.isSubmitting} type="submit">
                                            Update
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </Form>
                        );
                    }}
                </Formik>
            </Modal>

            <Modal show={showConfirmation}>
                <Modal.Header onHide={() => setShowConfirmation(false)}>
                    <Heading level={2}>Unsaved changes</Heading>
                </Modal.Header>
                <Modal.Body>Are you sure you want to discard the changes you have made?</Modal.Body>
                <Modal.Footer>
                    <div className="flex justify-between">
                        <Button onClick={() => setShowConfirmation(false)} variant="tertiary">
                            Continue editing
                        </Button>
                        <Button
                            onClick={() => {
                                setShowConfirmation(false);
                                onCloseEditModal();
                            }}
                        >
                            Discard
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}
